import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';

import { Button } from 'react-bootstrap';

import SVButton from 'components/elements/SVButton';
import FontAwesomeIcon from 'components/elements/FontAwesomeIcon';
import { FlexDivSpaceBetweenCenter } from 'resources/styles/display';
import useCurrentApplication from 'hooks/useCurrentApplication';
import {
  closeQuestionComments,
  addQuestionComment,
  loadMoreComments,
} from 'store/actions/comments';

import {
  SurveyCommentsContainer,
  SurveyCommentTxt,
  SurveyCommentUser,
  SurveyCommentsTitle,
} from './styles';

interface CommentItem {
  id: number | string;
  userFullName: string;
  message: string;
}

interface CommentsData {
  items: CommentItem[];
  total: number;
  page: number;
}

interface CurrentCommentsState {
  isLoading: boolean;
  question?: {
    id?: number;
    title?: string;
  };
  data: CommentsData;
}

interface RootState {
  comments: {
    currentComments: CurrentCommentsState;
  };
}

interface SurveyCommentsProps {
  surveyVersionId: number;
}

const SurveyComments: React.FC<SurveyCommentsProps> = ({ surveyVersionId }) => {
  const { appData, userTypeId } = useCurrentApplication();
  const dispatch = useDispatch<ThunkDispatch<any, void, AnyAction>>();

  const textInput = useRef<HTMLTextAreaElement>(null);

  const { isLoading, question, data } = useSelector(
    (state: RootState) => state.comments.currentComments,
  );

  const { items, total, page } = data || { items: [], total: 0, page: 0 };

  const clearTextArea = () => {
    if (textInput.current) {
      textInput.current.value = '';
    }
  };

  const handleCommentAdd = () => {
    if (!textInput.current) return;

    const message = textInput.current.value.trim();
    if (!message) return;

    dispatch(
      addQuestionComment({
        applicationId: appData.id,
        surveyVersionId,
        userTypeId,
        questionId: question?.id,
        message,
        cb: clearTextArea,
      }),
    );
  };

  const handleMoreComments = () => {
    dispatch(
      loadMoreComments({
        page: page + 1,
        questionId: question?.id,
        applicationId: appData.id,
        userTypeId,
        surveyVersionId,
      }),
    );
  };

  if (!question?.id) return null;

  return (
    <SurveyCommentsContainer>
      <FlexDivSpaceBetweenCenter>
        <SurveyCommentsTitle>{question.title}</SurveyCommentsTitle>
        <Button variant="link" size="lg" onClick={() => dispatch(closeQuestionComments())}>
          <FontAwesomeIcon className="fa-times-circle-o" />
        </Button>
      </FlexDivSpaceBetweenCenter>

      {isLoading ? (
        <FontAwesomeIcon className="fa-spinner fa-spin fa-2x" />
      ) : (
        <div>
          <div>
            <SurveyCommentTxt placeholder="Comment text" ref={textInput} />
            <SVButton size="sm" variant="secondary" onClick={handleCommentAdd}>
              Add
            </SVButton>
          </div>

          {items?.map((item) => (
            <div key={item.id} className="pt-3">
              <SurveyCommentUser>{item.userFullName}</SurveyCommentUser>
              <div>{item.message}</div>
            </div>
          ))}

          {items?.length < total && (
            <SVButton size="sm" onClick={handleMoreComments}>
              Load more comments
            </SVButton>
          )}
        </div>
      )}
    </SurveyCommentsContainer>
  );
};

export default SurveyComments;
